import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Loader2, Send } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { messageErreurInvitation, useInviteAgent } from '@/api/team'

const schema = z.object({
  email: z.string().email('email_invalid'),
})

type FormValues = z.infer<typeof schema>

interface Props {
  open: boolean
  onClose: () => void
}

export function InviteAgentDialog({ open, onClose }: Props) {
  const { t } = useTranslation()
  const invite = useInviteAgent()

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '' },
  })

  const onSubmit = async (values: FormValues) => {
    try {
      await invite.mutateAsync({ email: values.email })
      toast.success(t('manager.team.invite.success', { email: values.email }))
      form.reset()
      onClose()
    } catch (err: unknown) {
      const data = (
        err as { response?: { data?: { error?: string; message?: string } } }
      )?.response?.data
      toast.error(messageErreurInvitation(data, t))
    }
  }

  const handleOpenChange = (next: boolean) => {
    if (!next && !invite.isPending) {
      form.reset()
      onClose()
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <DialogHeader>
            <DialogTitle>{t('manager.team.invite.title')}</DialogTitle>
            <DialogDescription>
              {t('manager.team.invite.description')}
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-2">
            <Label htmlFor="invite-email">
              {t('manager.team.invite.emailLabel')}
            </Label>
            <Input
              id="invite-email"
              type="email"
              placeholder="agent@entreprise.ca"
              autoFocus
              {...form.register('email')}
            />
            {form.formState.errors.email && (
              <p className="text-xs text-red-500">
                {t(
                  `manager.team.invite.errors.${form.formState.errors.email.message}`,
                )}
              </p>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => handleOpenChange(false)}
              disabled={invite.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              disabled={invite.isPending}
              className="bg-[#0A192F] text-white hover:bg-[#0A192F]/90 dark:bg-white dark:text-[#0A192F] dark:hover:bg-white/90"
            >
              {invite.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              {t('manager.team.invite.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
